import React from 'react';
import { Sparkles, Award } from 'lucide-react';

/**
 * FoundingCohortBadge — small visual indicator for users with
 * `foundingCohort: true` on their user document.
 *
 * Set automatically on signup for the first 100 host signups. Perks
 * documented in memory/marketing/founding-cohort.md.
 *
 * Two display modes:
 *   - inline (default): pill badge for placement next to the user's name
 *   - card:             larger gradient card for dashboard headers
 */
const FoundingCohortBadge = ({ variant = 'inline', startedAt, size = 'md', className = '' }) => {
  // Compute "joined X days ago" if we have the timestamp
  let joinedLabel = '';
  if (startedAt) {
    const diffDays = Math.floor((Date.now() - new Date(startedAt).getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) joinedLabel = 'joined today';
    else if (diffDays === 1) joinedLabel = 'joined yesterday';
    else if (diffDays < 30) joinedLabel = `joined ${diffDays} days ago`;
    else joinedLabel = `joined ${Math.floor(diffDays / 30)}mo ago`;
  }

  if (variant === 'card') {
    return (
      <div
        className={`relative overflow-hidden rounded-xl bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 border border-amber-200 p-4 ${className}`}
        data-testid="founding-cohort-badge-card"
      >
        <div className="absolute top-0 right-0 w-32 h-32 bg-amber-200/40 rounded-full blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
            <Award size={20} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-amber-900">Founding Member</span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-700 border border-amber-500/30">
                first 100
              </span>
            </div>
            <p className="text-sm text-amber-800/80 mt-1 leading-snug">
              0% platform fee for 90 days, then 10% for the rest of year one · priority marketplace placement · founding member badge
            </p>
            {joinedLabel && (
              <p className="text-xs text-amber-700/70 mt-1.5">{joinedLabel}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Inline pill variant
  const sizes = {
    sm: 'text-[10px] px-1.5 py-0.5 gap-1',
    md: 'text-xs px-2 py-1 gap-1.5',
    lg: 'text-sm px-3 py-1.5 gap-2',
  };
  const iconSize = { sm: 10, md: 12, lg: 14 };

  return (
    <span
      className={`inline-flex items-center rounded-full font-medium bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 border border-amber-300 ${sizes[size]} ${className}`}
      data-testid="founding-cohort-badge-inline"
      title="Founding Member — one of Ariv's first 100 hosts"
    >
      <Sparkles size={iconSize[size]} className="text-amber-600" />
      <span>Founding Member</span>
    </span>
  );
};

export default FoundingCohortBadge;
